/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import { withRouter } from "../_shared/withRouter";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LearnerAuthShell from "./learnerAuthShell";
import InputComponent from "../template/_common/inputComponent";
import { globalAlert } from "../actions/commonActions";
import { passwordPolicyOk, PASSWORD_MIN, resolveLmsShortName, learnerPath } from "./_learnerConfig";
import { learnerResetPasswordApi, learnerPortalInfoApi } from "./_learnerActions";

const REDIRECT_MS = 2000;

// Set / reset password + account activation. The backend emails a link of the
// form `{scheme}://{host}/e-learning/{shortName}/set-password?token=…&email=…`
// — the tenant comes from the path (resolveLmsShortName), token+email from the
// query. This one page serves BOTH the activation link and the forgot-password
// reset link — identical flow, one endpoint (POST /public/reset-password).
//
// Backend-verified contract (NOT the /portal-info + x-lms-short-name header the
// generic spec assumes): tenant is sent as `lmsShortName` in the query/body (the
// custom header is not honoured and would risk a CORS preflight); org lookup is
// `GET /public/portal?lmsShortName=…` → data.organisation{name}; reset returns
// the {status,data,message} envelope with 204 as a SOFT error to render inline.
const LearnerSetPassword = (props) => {
  const { t } = useTranslation();
  const params = useMemo(
    () => new URLSearchParams(props?.location?.search || ""),
    [props?.location?.search]
  );
  const token = params.get("token") || "";
  const email = params.get("email") || "";
  const missingLink = !token || !email;

  const [org, setOrg] = useState(null);
  // Link validity: missing token/email, or a portal 204 (unknown/short name not
  // in the path), makes the whole link dead. A portal network error does NOT —
  // we still let them try.
  const [linkInvalid, setLinkInvalid] = useState(missingLink);
  const [checking, setChecking] = useState(!missingLink);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [forceShowRules, setForceShowRules] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const passwordRules = [
    {
      key: "min",
      label: t("learner.lms.setPassword.ruleMin", "At least 8 characters"),
      ok: password.length >= PASSWORD_MIN,
    },
    {
      key: "upper",
      label: t("learner.lms.setPassword.ruleUpper", "One capital letter"),
      ok: /[A-Z]/.test(password),
    },
    {
      key: "lower",
      label: t("learner.lms.setPassword.ruleLower", "One small letter"),
      ok: /[a-z]/.test(password),
    },
    {
      key: "number",
      label: t("learner.lms.setPassword.ruleNumber", "One number"),
      ok: /[0-9]/.test(password),
    },
    {
      key: "special",
      label: t("learner.lms.setPassword.ruleSpecial", "One special character"),
      ok: /[^A-Za-z0-9]/.test(password),
    },
  ];

  // Resolve which org the learner is activating for (from the path short name).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await learnerPortalInfoApi({
          lmsShortName: resolveLmsShortName(),
        });
        const { status, data } = res?.data || {};
        if (!alive) return;
        if (status === 200) setOrg(data?.organisation || null);
        else if (status === 204) setLinkInvalid(true); // tenant short name/link is invalid
      } catch (e) {
        /* network error — keep the form usable, just without the org name */
      }
      if (alive) setChecking(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Success → auto-redirect to the learner login after a short beat.
  useEffect(() => {
    if (!done) return;
    const id = setTimeout(() => props?.history?.push(learnerPath(resolveLmsShortName(), "login")), REDIRECT_MS);
    return () => clearTimeout(id);
  }, [done]);

  const allRulesOk = passwordRules.every((r) => r.ok);
  const isPopoverShowing = (showRules || forceShowRules) && !allRulesOk && password.length > 0;

  const validate = () => {
    const e = {};
    if (!password) {
      e.password = t("learner.lms.errors.newPasswordRequired", "New password is required");
    } else if (!passwordPolicyOk(password)) {
      e.password = t(
        "learner.lms.errors.passwordPolicy",
        "Password must include at least {{n}} characters, one capital letter, one small letter, one number and one special character.",
        { n: PASSWORD_MIN }
      );
    }

    if (!confirm) {
      e.confirm = t("learner.lms.errors.confirmPasswordRequired", "Confirm password is required");
    } else if (confirm !== password) {
      e.confirm = t("learner.lms.errors.passwordMismatch", "Passwords must match.");
    }
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      if (e.password && !allRulesOk) {
        setForceShowRules(true);
      }
      document.querySelector(`[name="${e.password ? "password" : "confirmPassword"}"]`)?.focus();
      return;
    }
    setBusy(true);
    try {
      // token + email come from the query string; kept in memory only.
      const res = await learnerResetPasswordApi({ email, token, password });
      const { status, message } = res?.data || {};
      if (status === 200) {
        setErrors({});
        setDone(true);
      } else if (status === 204) {
        // Soft error — bind message to the form (mandatory fields, invalid/expired
        // token, or the server password-policy message). Never throw on 204.
        setErrors({ form: message || t("learner.lms.errors.tokenInvalid", "This link is invalid or has expired.") });
      } else {
        globalAlert("error", message);
      }
    } catch (err) {
      globalAlert("error", err?.response?.data?.message);
    }
    setBusy(false);
  };

  const backToLogin = (
    <Link to={learnerPath(resolveLmsShortName(), "login")} className="lms-auth-link text-decoration-none" style={{ fontSize: '1.1rem' }}>
      <i className="fas fa-arrow-left me-2"></i>
      {t("learner.lms.forgot.backToLogin", "Back to Login")}
    </Link>
  );

  /* ── invalid / expired link ── */
  if (missingLink || linkInvalid) {
    return (
      <LearnerAuthShell
        title={t("learner.lms.setPassword.title", "Set your password")}
        heading={t("learner.lms.setPassword.badLinkHeading", "Link not valid")}
        organisation={org}
        footer={backToLogin}
      >
        <div className="lms-auth-banner error" role="alert">
          {t(
            "learner.lms.setPassword.badLink",
            "This link is invalid or has expired. Please request a new one."
          )}
        </div>
      </LearnerAuthShell>
    );
  }

  /* ── validating the tenant path / link ── */
  if (checking) {
    return (
      <LearnerAuthShell
        title={t("learner.lms.setPassword.title", "Set your password")}
        organisation={org}
      >
        <div className="lms-auth-checking text-center py-3" role="status">
          <i className="fas fa-circle-notch fa-spin mr-2"></i>
          {t("learner.lms.setPassword.checking", "Verifying your link…")}
        </div>
      </LearnerAuthShell>
    );
  }

  /* ── success ── */
  if (done) {
    return (
      <LearnerAuthShell
        title={t("learner.lms.setPassword.title", "Set your password")}
        heading={t("learner.lms.setPassword.successTitle", "Password set")}
        organisation={org}
        footer={backToLogin}
      >
        <div className="lms-auth-success text-center" role="status" aria-live="polite">
          <span className="lms-auth-success-icon">
            <i className="fas fa-check-circle"></i>
          </span>
          <p className="lms-auth-success-text">
            {t(
              "learner.lms.setPassword.successBody",
              "Your password has been set — you can now log in."
            )}
          </p>
          <p className="lms-auth-hint small">
            {t("learner.lms.setPassword.redirecting", "Taking you to the login page…")}
          </p>
          <Link to={learnerPath(resolveLmsShortName(), "login")} className="btn primary-btn lms-auth-submit mt-2">
            {t("learner.lms.setPassword.goToLogin", "Go to login")}
          </Link>
        </div>
      </LearnerAuthShell>
    );
  }

  /* ── the form ── */
  return (
    <LearnerAuthShell
      title={t("learner.lms.setPassword.title", "Set your password")}
      heading={t("learner.lms.setPassword.title", "Set your password")}
      organisation={org}
      subheading={
        org?.name
          ? t("learner.lms.setPassword.subOrg", "Activate your account for {{org}} — choose a password for {{email}}", {
              org: org.name,
              email,
            })
          : t("learner.lms.setPassword.sub", "Choose a password for {{email}}", { email })
      }
      footer={backToLogin}
    >
      <form onSubmit={submit} className="form-own lms-auth-form" noValidate>
        <div className="form-group reg-date login-page position-relative">
          <InputComponent
            placeholder={t("learner.lms.fields.newPassword", "New password")}
            label={`${t("learner.lms.fields.newPassword", "New password")} *`}
            name="password"
            type={showPw ? "text" : "password"}
            theme="primary"
            color="#d7eff5"
            maxLength={30}
            value={password}
            autoComplete="new-password"
            handleCallback={(val) => {
              setPassword(val);
              setShowRules(true);
              setForceShowRules(false);
              if (errors.password) setErrors((p) => ({ ...p, password: "" }));
            }}
            onFocus={() => setShowRules(true)}
            onBlur={() => setShowRules(false)}
          />
          <button
            type="button"
            className="login-password lms-password-toggle cursor"
            aria-label={showPw ? t("learner.lms.hidePassword", "Hide password") : t("learner.lms.showPassword", "Show password")}
            aria-pressed={showPw}
            onClick={() => setShowPw((v) => !v)}
          >
            <i className={`fas ${showPw ? "fa-eye" : "fa-eye-slash"}`} aria-hidden="true"></i>
          </button>
          {isPopoverShowing && (
            <div className="lms-password-popover" id="sp-policy">
              <ul className="lms-password-rules">
                {passwordRules.map((rule) => (
                  <li key={rule.key} className={`${rule.ok ? "active" : ""} ${forceShowRules && !rule.ok ? "text-danger" : ""}`}>
                    <i className={`fas ${rule.ok ? "fa-check-circle" : forceShowRules ? "fa-times-circle text-danger" : "fa-circle"}`}></i>
                    <span>{rule.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {errors.password && !isPopoverShowing && (
            <div id="sp-password-err" className="error-text font-12 text-start pl-2 mt-1" role="alert">
              {errors.password}
            </div>
          )}
        </div>

        <div className="form-group reg-date login-page position-relative">
          <InputComponent
            placeholder={t("learner.lms.fields.confirmPassword", "Confirm password")}
            label={`${t("learner.lms.fields.confirmPassword", "Confirm password")} *`}
            name="confirmPassword"
            type={showConfirmPw ? "text" : "password"}
            theme="primary"
            color="#d7eff5"
            maxLength={30}
            value={confirm}
            autoComplete="new-password"
            handleCallback={(val) => {
              setConfirm(val);
              if (errors.confirm) setErrors((p) => ({ ...p, confirm: "" }));
            }}
          />
          <button
            type="button"
            className="login-password lms-password-toggle cursor"
            aria-label={showConfirmPw ? t("learner.lms.hidePassword", "Hide password") : t("learner.lms.showPassword", "Show password")}
            aria-pressed={showConfirmPw}
            onClick={() => setShowConfirmPw((v) => !v)}
          >
            <i className={`fas ${showConfirmPw ? "fa-eye" : "fa-eye-slash"}`} aria-hidden="true"></i>
          </button>
          {errors.confirm && (
            <div id="sp-confirm-err" className="error-text font-12 text-start pl-2 mt-1" role="alert">
              {errors.confirm}
            </div>
          )}
        </div>

        {errors.form && (
          <div className="lms-auth-banner error" role="alert">
            {errors.form}
          </div>
        )}

        <button type="submit" className="btn primary-btn btn-block lms-auth-submit w-100 mt-3" disabled={busy}>
          {busy ? (
            <i className="fas fa-circle-notch fa-spin"></i>
          ) : (
            t("learner.lms.setPassword.button", "Set password")
          )}
        </button>
      </form>
    </LearnerAuthShell>
  );
};

export default withRouter(LearnerSetPassword);
